import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { consumeRecoveryCode } from '@/lib/api/endpoints';
import { toUserMessage } from '@/lib/api/error';
import { useUserAuth } from '@/lib/auth/UserAuthContext';

/**
 * Lets a user sign in with their one-time recovery code.
 * On success the backend burns the old code and issues a fresh one,
 * which the welcome modal in the Shell surfaces once.
 */
export function Recover(): React.JSX.Element {
  const { applySession } = useUserAuth();
  const navigate = useNavigate();
  const [code, setCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting || code.trim().length === 0) return;
      setSubmitting(true);
      setError(null);
      void (async () => {
        try {
          const payload = await consumeRecoveryCode({ code: code.trim() });
          applySession(payload);
          navigate('/today', { replace: true });
        } catch (err) {
          setError(toUserMessage(err, 'Could not use this recovery code.'));
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [applySession, code, navigate, submitting],
  );

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Use a recovery code</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <p className="text-caption text-muted-foreground">
              Enter the one-time code we showed you when you first signed up. Whitespace and
              capitalisation are ignored.
            </p>
            <div className="space-y-1">
              <Label htmlFor="recovery-code">Recovery code</Label>
              <Input
                id="recovery-code"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                disabled={submitting}
                required
              />
            </div>
            {error ? (
              <p role="alert" className="text-caption text-destructive">
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || code.trim().length === 0}
            >
              {submitting ? 'Checking…' : 'Use recovery code'}
            </Button>
            <p className="text-caption text-muted-foreground">
              Remember your email?{' '}
              <Link to="/signin" className="underline">
                Email sign-in
              </Link>
              .
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
